import * as React from 'react'
import type { CommonDeps } from '../../../app-core/lib/base/CommonDeps'
import type { AppGuide, ParentInfo } from '../../../app-core/lib/guide/Model'
import type { RenderCachedImage } from '../../../app-core/lib/image/WithCachedImage'
import {
    isAiChatFileSource,
    isAiChatGuideSource,
    type AiChatGuideSource,
    type AiChatSource,
} from '../../../shared/domain/aiChats/types.no-deps'
import { GuideSource } from './ZapBotGuideSource'
import { ReportSource } from './ZapBotReportSource'
import { ChatFileSource } from './ZapBotChatFileSource'
import { type ThumbnailUrls, fetchS3Urls } from '../file/withS3Url'

type Props = {
    sources: AiChatSource[]
    deps: CommonDeps
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
    renderCachedImage: RenderCachedImage
    onSourceClick: (source: AiChatSource, downloadUrl?: string) => void
    fetchAppGuides: (guideSources: AiChatGuideSource[], withParentInfo: boolean) => Promise<AppGuide[]>
}

export function SourceList({
    sources,
    deps,
    onClickGuideParentInfo,
    renderCachedImage,
    onSourceClick,
    fetchAppGuides,
}: Props) {
    const [appGuides, setAppGuides] = React.useState<AppGuide[]>([])
    const [chatFileUrls, setChatFileUrls] = React.useState<ThumbnailUrls>({})

    React.useEffect(() => {
        const guideSources = sources.filter(isAiChatGuideSource)

        fetchAppGuides(guideSources, true).then(setAppGuides)

        const chatFileIds = sources.filter(isAiChatFileSource).map((source) => source.file.fileId)
        fetchS3Urls(deps, chatFileIds, false).then(setChatFileUrls)
    }, [sources])

    return (
        <ul className="ZapBot_source-list">
            {sources.map((source, index) => {
                switch (source.type) {
                    case 'guide':
                        const appGuide = appGuides.find((ag) => ag.id === source.guide.id)
                        return (
                            appGuide && (
                                <li key={appGuide.id} className="ZapBot_source">
                                    <GuideSource
                                        source={source}
                                        number={index + 1}
                                        appGuide={appGuide}
                                        onClick={() => onSourceClick(source)}
                                        deps={deps}
                                        onClickParentInfo={onClickGuideParentInfo}
                                        renderCachedImage={renderCachedImage}
                                    />
                                </li>
                            )
                        )
                    case 'chatFile':
                        return (
                            chatFileUrls[source.file.fileId] && (
                                <li key={source.file.fileId} className="ZapBot_source">
                                    <ChatFileSource
                                        source={source}
                                        number={index + 1}
                                        onClick={() => onSourceClick(source, chatFileUrls[source.file.fileId])}
                                        deps={deps}
                                    />
                                </li>
                            )
                        )

                    case 'report':
                        return (
                            <li key={source.report.clientId} className="ZapBot_source">
                                <ReportSource
                                    source={source}
                                    number={index + 1}
                                    onClick={() => onSourceClick(source)}
                                    deps={deps}
                                />
                            </li>
                        )
                }
            })}
        </ul>
    )
}
